import React, { useRef } from 'react';
import { Circle, Group } from 'react-konva';
import type { KonvaEventObject } from 'konva/lib/Node';
import { useDragGesture } from 'src/hooks/useDragGesture';

interface Point {
    x: number;
    y: number;
}

const cursorStyles = ['nw-resize', 'ne-resize', 'se-resize', 'sw-resize'];

interface Props {
    points: Point[];
    onDragStart: (initialPosition: Point) => void;
    onDragMove: (points: Point[]) => void;
    onDragEnd: (points: Point[]) => void;
    offset: number;
    width?: number;
    height?: number;
    color?: string;
}

const GrabPoints: React.FC<Props> = ({
    points,
    onDragStart: onDragStartProp,
    onDragMove: onDragMoveProp,
    onDragEnd: onDragEndProp,
    offset,
    width,
    height,
    color,
}) => {
    // Snapshot-at-drag-start bookkeeping (the initial mouse position and which point is being
    // dragged) -- can't be derived from the current render, so it stays in a ref. `localPointsRef`
    // holds the most recently computed drag position so `onDragEnd` can commit it -- this replaces
    // a module-level `let localPoints` variable the original had, which was shared across *every*
    // instance of this component rather than scoped to one. That was harmless only because
    // there's realistically one Cropping rectangle on screen at a time; it wasn't a safe pattern.
    const initialPositionRef = useRef<Point>({ x: 0, y: 0 });
    const dragIndexRef = useRef(0);
    const localPointsRef = useRef<Point[]>([]);
    // `points` as they were when the drag began: deltas are measured from the initial mouse position,
    // so they must be applied to the start points, not the live prop that onDragMove keeps updating.
    const startPointsRef = useRef<Point[]>(points);

    const handleDragMove = (e: Event) => {
        const evt = e as MouseEvent;
        const { x: initialX, y: initialY } = initialPositionRef.current;
        const index = dragIndexRef.current;
        const deltaX = initialX - evt.clientX;
        const deltaY = initialY - evt.clientY;

        const startPoints = startPointsRef.current;
        const selected = startPoints[index];
        const newPoints = startPoints.map((point) => {
            const newPoint = { ...point };

            // setting points to new coordinates
            if (point.x === selected.x) {
                // restrict it to be within width bounds
                newPoint.x = width ? Math.min(Math.max(offset, point.x - deltaX), width + offset) : point.x - deltaX;
            }
            if (point.y === selected.y) {
                // restrict it to be within height bounds
                newPoint.y = height ? Math.min(Math.max(offset, point.y - deltaY), height + offset) : point.y - deltaY;
            }
            return newPoint;
        });

        localPointsRef.current = [newPoints[0], newPoints[2]];

        onDragMoveProp([newPoints[0], newPoints[2]]);
    };

    const { start } = useDragGesture({
        onDragMove: handleDragMove,
        onDragEnd: () => onDragEndProp(localPointsRef.current),
    });

    const onDragStart = (e: KonvaEventObject<MouseEvent>, index: number) => {
        const initialPosition = {
            x: e.evt.clientX,
            y: e.evt.clientY,
        };

        initialPositionRef.current = initialPosition;
        dragIndexRef.current = index;
        startPointsRef.current = points;
        // a click without a move commits the unchanged crop, not an empty one
        localPointsRef.current = [points[0], points[2]];

        start();
        e.cancelBubble = true;
        onDragStartProp(initialPosition);
    };

    const cursorChange = (cursorStyle: string) => {
        document.body.style.cursor = cursorStyle;
    };

    return (
        <Group>
            {points.map((point, index) => (
                <Circle
                    // eslint-disable-next-line react/no-array-index-key
                    key={index}
                    x={point.x}
                    y={point.y}
                    radius={10}
                    fill={color || 'black'}
                    stroke={'white'}
                    strokeWidth={1}
                    onMouseEnter={() => cursorChange(cursorStyles[index])}
                    onMouseLeave={() => cursorChange('default')}
                    onMouseDown={(e) => onDragStart(e, index)}
                />
            ))}
        </Group>
    );
};

export default GrabPoints;
